'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
} from 'react';
import { useLanguagePass } from '@/components/i18n/LanguagePass';
import { Icon } from '@/components/ui/Icon';
import { localeMeta, locales, type Locale } from '@/i18n/config';
import { swapLocale } from '@/i18n/routing';
import { useLocale } from '@/i18n/useLocale';
import { cn } from '@/lib/cn';

/**
 * The language selector.
 *
 * Built as a *position* indicator rather than a decorated dropdown, because
 * that is what choosing one of three fixed options is. The trigger carries a
 * three-segment index — one segment per language, in the same order as the
 * panel below it — and the lit segment is where you currently are. Switching
 * moves the light. Nothing about it is ornamental: read the index and you know
 * the state without opening anything.
 *
 * The panel echoes the rail-and-node language the process section already uses:
 * a hairline down the left with a node that tracks whichever row the pointer or
 * the keyboard is on. Same idea as a station on a line, at a tenth the size.
 *
 * The rows are real links to the same page in another language — crawlable,
 * `hrefLang`-tagged, and fully working with JavaScript off. A plain left-click
 * is upgraded to the language pass; a modified click (new tab) keeps native
 * behaviour, exactly as `OrderCta` does with the order curtain.
 */

/** Matches the panel's exit animation in globals.css. */
const CLOSE_MS = 170;

type LanguageSwitcherProps = {
  /** `switcher.*` strings, in the language currently on screen. */
  labels: { label: string; trigger: string; current: string };
  /** The three language names, in the language currently on screen. */
  names: Record<Locale, string>;
  className?: string;
};

/**
 * The panel remembers *which* route it was opened on, so a navigation closes it
 * during render rather than from an effect — the same trick `MobileMenu` uses.
 * That matters most for the case this component creates: a finished language
 * switch is a navigation, and the panel has to be shut on the other side of it.
 */
type PanelState = { phase: 'open' | 'closing'; on: string };

export function LanguageSwitcher({
  labels,
  names,
  className,
}: LanguageSwitcherProps) {
  const active = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const startPass = useLanguagePass();

  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const rowsRef = useRef<(HTMLAnchorElement | null)[]>([]);

  const [panel, setPanel] = useState<PanelState | null>(null);
  /** Whether the panel was opened from the keyboard — see `toggle`. */
  const openedByKey = useRef(false);
  /* Null on any route other than the one the panel was opened on. */
  const visible = panel?.on === pathname ? panel : null;
  const open = visible?.phase === 'open';

  const activeIndex = Math.max(locales.indexOf(active), 0);
  /** Which row the rail's node is parked on — pointer, focus, then reality. */
  const [cursor, setCursor] = useState(activeIndex);

  const close = useCallback((returnFocus = false) => {
    setPanel((previous) =>
      previous?.phase === 'open'
        ? { phase: 'closing', on: previous.on }
        : previous,
    );
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  /** The other two languages, warmed up so the swap lands under the veil. */
  const prefetchAlternates = useCallback(() => {
    for (const locale of locales) {
      if (locale !== active) router.prefetch(swapLocale(pathname, locale));
    }
  }, [active, pathname, router]);

  const toggle = (event: MouseEvent<HTMLButtonElement>) => {
    if (open) {
      close();
      return;
    }
    /*
     * `detail === 0` is a click the browser synthesised from Enter or Space on
     * the button — there was no pointer. That is the one case where focus
     * should move into the panel: a keyboard user has nothing else to steer
     * with. A mouse user keeps focus on the trigger, so the current row is not
     * suddenly wearing a focus ring they did not ask for.
     */
    openedByKey.current = event.detail === 0;
    setCursor(activeIndex);
    setPanel({ phase: 'open', on: pathname });
    prefetchAlternates();
  };

  // Unmount the panel once its exit animation has played.
  useEffect(() => {
    if (visible?.phase !== 'closing') return;
    const timer = window.setTimeout(() => setPanel(null), CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [visible?.phase]);

  // A keyboard open hands over the current selection, so the arrow keys then
  // walk the list from where the reader actually is.
  useEffect(() => {
    if (!open || !openedByKey.current) return;
    rowsRef.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  // Escape, arrow keys, and clicks landing outside.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
        return;
      }

      const step =
        event.key === 'ArrowDown' || event.key === 'ArrowRight'
          ? 1
          : event.key === 'ArrowUp' || event.key === 'ArrowLeft'
            ? -1
            : 0;

      if (step !== 0) {
        event.preventDefault();
        const from = rowsRef.current.findIndex(
          (row) => row === document.activeElement,
        );
        // Wraps, so holding one arrow key cycles rather than dead-ending.
        const next =
          (((from === -1 ? activeIndex : from) + step) % locales.length +
            locales.length) %
          locales.length;
        rowsRef.current[next]?.focus();
        setCursor(next);
        return;
      }

      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : locales.length - 1;
        rowsRef.current[next]?.focus();
        setCursor(next);
      }
    };

    const onPointerDown = (event: PointerEvent) => {
      const node = event.target;
      if (node instanceof Node && rootRef.current?.contains(node)) return;
      close();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, close, activeIndex]);

  const handleRowClick =
    (locale: Locale) => (event: MouseEvent<HTMLAnchorElement>) => {
      if (locale === active) {
        // Already reading this language: the link would be a no-op navigation.
        event.preventDefault();
        close(true);
        return;
      }

      if (
        !startPass ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault();
      close();
      startPass(locale);
    };

  return (
    <div ref={rootRef} className={cn('lang', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        onPointerEnter={prefetchAlternates}
        onFocus={prefetchAlternates}
        aria-expanded={open}
        aria-controls={panelId}
        // The visible code is two letters; the accessible name says what the
        // control is and which language is set.
        aria-label={`${labels.trigger}. ${labels.current}: ${names[active]}`}
        className="lang__trigger"
        data-open={open || undefined}
      >
        <span aria-hidden="true" className="lang__index">
          {locales.map((locale) => (
            <span
              key={locale}
              className="lang__seg"
              data-on={locale === active || undefined}
            />
          ))}
        </span>

        <span className="lang__code">{localeMeta[active].code}</span>

        <Icon
          name="chevronDown"
          size={14}
          strokeWidth={2.5}
          className="lang__chev"
        />
      </button>

      <div
        id={panelId}
        hidden={visible === null}
        data-closing={visible?.phase === 'closing' || undefined}
        className="lang__panel"
      >
        <p className="lang__title label-xs">{labels.label}</p>

        <div className="lang__body">
          <span aria-hidden="true" className="lang__rail">
            <span
              className="lang__node"
              style={{ '--at': cursor } as CSSProperties}
            />
          </span>

          <ul
            className="lang__list"
            onPointerLeave={() => setCursor(activeIndex)}
          >
            {locales.map((locale, index) => {
              const isActive = locale === active;
              /*
               * On its own row a language names itself twice — the panel in
               * Russian calls Russian «Русский», which is also its endonym. The
               * second line only earns its space when it says something new.
               */
              const alsoKnownAs =
                names[locale] === localeMeta[locale].endonym
                  ? null
                  : names[locale];
              return (
                <li
                  key={locale}
                  style={{ '--r': index } as CSSProperties}
                  className="lang__item"
                >
                  <Link
                    ref={(node) => {
                      rowsRef.current[index] = node;
                    }}
                    href={swapLocale(pathname, locale)}
                    hrefLang={localeMeta[locale].hreflang}
                    lang={localeMeta[locale].hreflang}
                    aria-current={isActive ? 'true' : undefined}
                    onClick={handleRowClick(locale)}
                    onPointerEnter={() => setCursor(index)}
                    onFocus={() => setCursor(index)}
                    className="lang__row"
                    data-active={isActive || undefined}
                  >
                    <span aria-hidden="true" className="lang__row-code">
                      {localeMeta[locale].code}
                    </span>

                    <span className="lang__row-text">
                      <span className="lang__endonym">
                        {localeMeta[locale].endonym}
                      </span>
                      {/* The endonym is the label; this is what the language is
                          called in the language you are reading right now. */}
                      {alsoKnownAs ? (
                        <span className="lang__native">{alsoKnownAs}</span>
                      ) : null}
                    </span>

                    {isActive ? (
                      <Icon
                        name="check"
                        size={15}
                        strokeWidth={2.75}
                        className="lang__check"
                      />
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
